import {
  parseAsActiveStatus,
  parseAsString,
  parseAsArrayOf,
  parseAsStringLiteral,
  useQueryStates,
  type HistoryOptions,
} from '@vrplatform/query-params';
import { useCallback, useMemo } from 'react';
import {
  useTableStoreFilter,
  useTableStoreFilterType,
  useTableStoreSetAllPagesSelected,
  useTableStoreSetFilter,
  useTableStoreSetSelected,
} from '../../../stores';
import type { api } from '@vrplatform/data';

type BusinessModelEnum = NonNullable<
  NonNullable<
    api['/reservations']['get']['parameters']['query']
  >['businessModel']
>;

const BUSINESS_MODELS = [
  'co_host',
  'co_host_airbnb',
  'managed',
] satisfies BusinessModelEnum[];

export const useReservationsTableFilter = () => {
  const [queryFilter, setQueryFilter] = useQueryStates({
    search: parseAsString,
    generalLedgerStatus: parseAsActiveStatus.withDefault('active'),
    status: parseAsString,
    paidStatus: parseAsString,
    listingIds: parseAsArrayOf(parseAsString),
    listingCollectionId: parseAsString,
    listingStatus: parseAsString,
    connectionIds: parseAsArrayOf(parseAsString),
    bookingChannels: parseAsArrayOf(parseAsString),
    date: parseAsString,
    dateType: parseAsString,
    businessModel: parseAsStringLiteral(BUSINESS_MODELS),
    lineType: parseAsString,
  });

  const filterType = useTableStoreFilterType();
  const stateFilter = useTableStoreFilter();
  const setStateFilter = useTableStoreSetFilter();
  const setSelection = useTableStoreSetSelected();
  const setAllPagesSelected = useTableStoreSetAllPagesSelected();

  const filter = useMemo(() => {
    if (filterType === 'query-params') {
      return queryFilter;
    }
    return stateFilter as typeof queryFilter;
  }, [filterType, stateFilter, queryFilter]);

  const setFilter = useCallback(
    (filter: Partial<typeof stateFilter>, updateType?: HistoryOptions) => {
      setSelection({});
      setAllPagesSelected(false);

      if (filterType === 'query-params') {
        setQueryFilter(filter, {
          history: updateType || 'push',
        });
      } else {
        setStateFilter(filter);
      }
    },
    [
      filterType,
      setQueryFilter,
      setStateFilter,
      setSelection,
      setAllPagesSelected,
    ]
  );

  const reset = useCallback(() => {
    const initial: Record<keyof typeof queryFilter, null> = {
      search: null,
      generalLedgerStatus: null,
      status: null,
      paidStatus: null,
      listingIds: null,
      listingCollectionId: null,
      listingStatus: null,
      connectionIds: null,
      bookingChannels: null,
      date: null,
      dateType: null,
      businessModel: null,
      lineType: null,
    };

    setStateFilter(initial);
    setQueryFilter(initial);
  }, [setStateFilter, setQueryFilter]);

  return {
    filter,
    setFilter,
    reset,
  };
};
